import localStorage from '@repositories/browserStorage/localStorage/LocalStorage';

// 상수 정의
const ROUTE_AFTER_LOGIN_KEY = 'routeAfterLogin';
const ROUTE_EXPIRY_MS = 10 * 60 * 1000; // 10분
const LOGIN_PATH = '/login';
const MANAGER_PREFIX = 'manager';

// 타입 정의
export interface RouteAfterLogin {
  path: string;
  tenantId?: string;
  timestamp: number;
}

/**
 * 타입을 검증하는 Type Guard
 */
const isRouteAfterLogin = (data: unknown): data is RouteAfterLogin => {
  // null, undefined 체크
  if (data === null || data === undefined) {
    return false;
  }

  // 객체인지 확인
  if (typeof data !== 'object') {
    return false;
  }

  // hasOwnProperty를 사용한 안전한 속성 체크
  const hasPath = Object.prototype.hasOwnProperty.call(data, 'path');
  const hasTimestamp = Object.prototype.hasOwnProperty.call(data, 'timestamp');

  if (!hasPath || !hasTimestamp) {
    return false;
  }

  // 각 속성의 타입 검증
  const pathValue = (data as { path?: unknown }).path;
  const timestampValue = (data as { timestamp?: unknown }).timestamp;
  const tenantIdValue = (data as { tenantId?: unknown }).tenantId;

  // path는 반드시 string
  if (typeof pathValue !== 'string') {
    return false;
  }

  // timestamp는 반드시 number
  if (typeof timestampValue !== 'number') {
    return false;
  }

  // tenantId는 선택적이지만, 존재하면 string이어야 함
  if (tenantIdValue !== undefined && typeof tenantIdValue !== 'string') {
    return false;
  }

  return true;
};

/**
 * 세션 만료 후 복귀할 경로를 localStorage에 저장
 * 로그인 페이지에서는 저장하지 않음
 */
export const saveRouteAfterLogin = (pathname: string): void => {
  const isLoginPage = pathname.includes(LOGIN_PATH);
  if (isLoginPage) {
    return;
  }

  const pathParts = pathname.split('/');
  const isManagerPage = pathParts[1] === MANAGER_PREFIX;
  const tenantId = isManagerPage && pathParts.length > 2 && pathParts[2] ? pathParts[2] : undefined;

  const routeData: RouteAfterLogin = {
    path: pathname,
    tenantId,
    timestamp: Date.now(),
  };

  localStorage.set(ROUTE_AFTER_LOGIN_KEY, JSON.stringify(routeData));
};

/**
 * localStorage에서 저장된 경로 정보를 가져오고 유효성을 검증
 * 유효하지 않거나 만료된 경우 자동으로 삭제하고 null 반환
 */
export const getValidRouteAfterLogin = (): RouteAfterLogin | null => {
  const routeData = localStorage.get<string>(ROUTE_AFTER_LOGIN_KEY);

  // 데이터가 없거나 string이 아닌 경우
  if (!routeData || typeof routeData !== 'string') {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(routeData);

    // Type Guard로 타입 검증
    if (!isRouteAfterLogin(parsed)) {
      localStorage.removeItem(ROUTE_AFTER_LOGIN_KEY);
      return null;
    }

    // 만료 시간 검증
    const isExpired = Date.now() - parsed.timestamp > ROUTE_EXPIRY_MS;
    if (isExpired) {
      localStorage.removeItem(ROUTE_AFTER_LOGIN_KEY);
      return null;
    }

    // 이제 parsed는 RouteAfterLogin 타입임이 보장됨
    return parsed;
  } catch {
    // JSON 파싱 실패
    localStorage.removeItem(ROUTE_AFTER_LOGIN_KEY);
    return null;
  }
};

/**
 * localStorage에서 저장된 경로 정보를 삭제
 */
export const clearRouteAfterLogin = (): void => {
  localStorage.removeItem(ROUTE_AFTER_LOGIN_KEY);
};
